(function (global, factory) {
  typeof exports === 'object' && typeof module !== 'undefined' ? factory(exports, require('react')) : typeof define === 'function' && define.amd ? define(['exports', 'react'], factory) : (global = global || self, factory(global.staircase = {}, global.React));
})(this, function (exports, react) {
  'use strict';

  const memoOne = func => {
    let lastArg;
    let lastRet;
    return arg => {
      if (lastArg === arg) {
        return lastRet;
      }

      lastArg = arg;
      lastRet = func(arg);
      return lastRet;
    };
  };

  function stepsAreEnhanced(steps) {
    return 'step' in steps[0];
  }

  const resolveStepsInner = steps => {
    if (!steps[0] || !stepsAreEnhanced(steps)) {
      return [steps, {}];
    } else {
      return steps.reduce((acc, cur, i) => {
        acc[0][i] = cur.step;
        acc[1][cur.name] = i;
        return acc;
      }, [[], {}]);
    }
  };
  /** @internal */


  const resolveSteps = memoOne(resolveStepsInner);

  function effectReducer(state, effect) {
    if (Array.isArray(effect)) {
      return effect;
    } else {
      return [...state, effect];
    }
  }
  /** @internal */


  function useSideEffectReducer(reducer, initialState) {
    const [effects, effectDispatch] = react.useReducer(effectReducer, []);
    const [state, innerDispatch] = react.useReducer(reducer, initialState);
    const wrappedDispatch = react.useCallback((action, cb) => {
      innerDispatch(action);

      if (cb) {
        effectDispatch(cb);
      }
    }, []);
    react.useEffect(() => {
      if (effects.length > 0) {
        const [effect, ...rest] = effects;
        effectDispatch(rest);
        effect(state);
      }
    }, [effects, state]);
    return [state, wrappedDispatch];
  }
  /** @internal */


  const useStabilizedFunction = function (fn) {
    const fnRef = react.useRef(fn);
    react.useEffect(() => {
      fnRef.current = fn;
    });
    return react.useCallback(function () {
      return fnRef.current(...arguments);
    }, []);
  };

  function reducer(state, action) {
    switch (action.type) {
      case 'updateValues':
        return Object.assign(Object.assign({}, state), {
          values: Object.assign(Object.assign({}, state.values), action.payload)
        });

      case 'setCanNext':
        return Object.assign(Object.assign({}, state), {
          canNext: action.payload.canNext
        });

      case 'setCanPrev':
        return Object.assign(Object.assign({}, state), {
          canPrev: action.payload.canPrev
        });

      case 'setCurrentStep':
        return Object.assign(Object.assign({}, state), {
          currentStep: action.payload.currentStep
        });

      case 'updateHistory':
        return Object.assign(Object.assign({}, state), {
          history: action.payload.history
        });

      case 'merge':
        return Object.assign(Object.assign({}, state), action.payload);
    }
  }

  function useStaircase(props) {
    const stepMapRef = react.useRef({});
    const latestStepPropsRef = react.useRef({});
    const [state, dispatch] = useSideEffectReducer(reducer, {
      currentStep: props.initialStepIndex || 0,
      canNext: true,
      canPrev: false,
      values: props.initialValues || {},
      history: []
    });
    const {
      currentStep
    } = state;
    const {
      steps,
      onChange: propsOnChange,
      values: propsValues,
      onStepChange
    } = props;
    const [resolvedSteps, stepMap] = resolveSteps(steps);
    stepMapRef.current = stepMap;

    const resolveStepIndex = step => {
      if (typeof step === 'number') {
        return step;
      }

      return stepMapRef.current[step];
    };

    const getChangeData = useStabilizedFunction(changes => {
      return Object.assign({
        currentStep: steps[state.currentStep],
        currentStepIndex: state.currentStep,
        values: Object.assign(Object.assign({}, propsValues || state.values), changes === null || changes === void 0 ? void 0 : changes.values)
      }, changes);
    }); // Validation

    const setCanNext = useStabilizedFunction(function () {
      let canNext = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : true;
      dispatch({
        type: 'setCanNext',
        payload: {
          canNext: !!canNext && currentStep < steps.length - 1
        }
      });
    });
    const setCanPrev = useStabilizedFunction(function () {
      let canPrev = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : true;
      dispatch({
        type: 'setCanPrev',
        payload: {
          canPrev: canPrev && currentStep > 0
        }
      });
    }); // Navigation

    const goToStep = useStabilizedFunction(function (step, meta) {
      let toPrevious = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
      const {
        currentStep
      } = state;
      const stepIndex = resolveStepIndex(step);
      const history = toPrevious ? state.history.slice(0, -1) : [...state.history, currentStep];
      const changes = {
        currentStep: steps[stepIndex],
        currentStepIndex: stepIndex,
        lastStepIndex: currentStep,
        direction: toPrevious ? 'prev' : 'next'
      };
      dispatch({
        type: 'merge',
        payload: {
          currentStep: stepIndex,
          canPrev: stepIndex > 0,
          canNext: stepIndex < steps.length - 1,
          history
        }
      }, state => onStepChange === null || onStepChange === void 0 ? void 0 : onStepChange(getChangeData(Object.assign(Object.assign({}, changes), {
        values: propsValues || state.values
      })), meta));
    });
    const next = useStabilizedFunction(meta => {
      const current = props.steps[state.currentStep];
      let nextStep;

      if ('step' in current && current.next) {
        nextStep = typeof current.next === 'function' ? current.next(propsValues || state.values, latestStepPropsRef.current) : current.next;
      } else {
        nextStep = Math.min(state.currentStep + 1, props.steps.length - 1);
      }

      goToStep(nextStep, meta, false);
    });
    const prev = useStabilizedFunction(meta => {
      const current = steps[state.currentStep];
      let prevStep;

      if ('step' in current && current.prev) {
        prevStep = typeof current.prev === 'function' ? current.prev(propsValues || state.values, latestStepPropsRef.current) : current.prev;
      } else {
        prevStep = state.history[state.history.length - 1] || 0;
      }

      goToStep(prevStep, meta, true);
    }); // Event Handlers

    const onChange = useStabilizedFunction((key, value, cb) => {
      // NB: if this is a controlled component, the last parameter of onChange will be called with
      // the _current_ values since we don't know how to update them.
      if (propsValues && propsOnChange) {
        propsOnChange(key, value, getChangeData());
      } else {
        dispatch({
          type: 'updateValues',
          payload: {
            [key]: value
          }
        }, (propsOnChange || cb) && (newState => {
          if (propsOnChange) {
            propsOnChange(key, value, getChangeData({
              values: newState.values
            }));
          }

          if (cb) {
            cb(newState.values);
          }
        }));
      }
    });
    const baseProps = {
      values: props.values || state.values,
      canNext: state.canNext,
      canPrev: state.canPrev,
      setCanNext,
      setCanPrev,
      next,
      prev,
      goToStep,
      onChange
    };

    const getStepProps = function () {
      let incomingProps = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
      latestStepPropsRef.current = incomingProps; // NB: onChange is overwritten because if provided it is called internally by Staircase#onChange

      return Object.assign(Object.assign(Object.assign({}, baseProps), incomingProps), {
        onChange
      });
    };

    return Object.assign(Object.assign({}, baseProps), {
      steps: resolvedSteps,
      Step: resolvedSteps[currentStep],
      currentStep,
      getStepProps,
      stepMap
    });
  }

  function Staircase(props) {
    const state = useStaircase(props);
    return props.children(state);
  }

  exports.Staircase = Staircase;
  exports.useStaircase = useStaircase;
  Object.defineProperty(exports, '__esModule', {
    value: true
  });
});